'use client'

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslations } from 'next-intl'
import HoverGradientButton from '@/components/buttons/HoverGradientButton/HoverGradientButton'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import './flagModal.css'

type Props = {
  productId: string
  productName: string
  formAction: (payload: FormData) => void
  isPending: boolean
  onClose: () => void
  onSubmit: () => void
}

export function FlagModal({ productId, productName, formAction, isPending, onClose, onSubmit }: Props) {
  const t = useTranslations('productCard')
  const [showOther, setShowOther] = useState(false)
  const contentRef = useFocusTrap<HTMLDivElement>(onClose)

  /* Portalled to <body> for the same reason as CompatibilityModal: the card sits
     inside .category-section's stacking context, which would otherwise pin the
     backdrop below the sticky products header. */
  const modal = (
    <div
      id="flag-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flag-modal-title"
      className="flag-modal-overlay"
      onClick={onClose}
    >
      <div className="flag-modal-backdrop" />
      <div
        ref={contentRef}
        className="flag-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="flag-modal-title" className="text-left mb-1">
          {t('flagModalTitle', { productName })}
        </h2>
        <p className="text-black/60 text-sm mb-5">{t('flagModalSubtitle')}</p>

        <form action={formAction} onSubmit={onSubmit}>
          <input type="hidden" name="productId" value={productId} />
          <input type="hidden" name="productName" value={productName} />

          <div className="flex flex-col gap-3 mb-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="flaggedReasons"
                value={t('flagReasonImage')}
                className="accent-orange mt-0.5 w-4 h-4 shrink-0"
              />
              <span className="text-sm text-black">{t('flagReasonImage')}</span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="flaggedReasons"
                value={t('flagReasonLink')}
                className="accent-orange mt-0.5 w-4 h-4 shrink-0"
              />
              <span className="text-sm text-black">{t('flagReasonLink')}</span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="flaggedReasons"
                value={t('flagReasonTypo')}
                className="accent-orange mt-0.5 w-4 h-4 shrink-0"
              />
              <span className="text-sm text-black">{t('flagReasonTypo')}</span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="flaggedReasons"
                value={t('flagReasonOther')}
                onChange={(e) => setShowOther(e.target.checked)}
                className="accent-orange mt-0.5 w-4 h-4 shrink-0"
              />
              <span className="text-sm text-black">{t('flagReasonOther')}</span>
            </label>

            {showOther && (
              <textarea
                name="flaggedReasonOther"
                placeholder={t('flagReasonOtherPlaceholder')}
                rows={3}
                maxLength={500}
                className="flag-modal-textarea"
              />
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <HoverGradientButton variant="outline" type="button" onClick={onClose}>
              {t('flagCancel')}
            </HoverGradientButton>
            <HoverGradientButton type="submit" disabled={isPending}>
              {t('flagSubmit')}
            </HoverGradientButton>
          </div>
        </form>

        <button
          onClick={onClose}
          aria-label={t('flagCancel')}
          className="flag-modal-close"
        >
          ✕
        </button>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
